import { useContext, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { UserContext } from "../contexts/user";
import {
    createShares,
    listShares,
    resendShare,
    revokeShare,
    FRONT_URL,
} from "../axios/axiosFunctions";

const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

const formatDate = (d) => {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleString();
    } catch {
        return "—";
    }
};

const statusFor = (s) => {
    if (s.revokedAt) return { label: "Revoked", color: "default" };
    if (s.lastAccessedAt) return { label: "Opened", color: "success" };
    if (s.lastSentAt) return { label: "Sent", color: "info" };
    return { label: "Pending", color: "default" };
};

const ManageReportAccessDialog = ({
    open,
    onClose,
    group,
    assessmentSlug,
    teamMembers,
}) => {
    const { currentUser } = useContext(UserContext);
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [alert, setAlert] = useState(null);
    const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
    const [externalRows, setExternalRows] = useState([{ name: "", email: "" }]);
    const [submitting, setSubmitting] = useState(false);

    const groupId = group?._id;

    const load = async () => {
        if (!groupId || !assessmentSlug) return;
        setLoading(true);
        const res = await listShares({ group: groupId, assessment: assessmentSlug });
        setLoading(false);
        if (!res.error) setShares(res.shares || []);
    };

    useEffect(() => {
        if (open) {
            load();
            setSelectedMemberIds(new Set());
            setExternalRows([{ name: "", email: "" }]);
            setAlert(null);
        }
    }, [open, groupId, assessmentSlug]);

    const sharedEmails = useMemo(
        () => new Set(shares.filter((s) => !s.revokedAt).map((s) => s.recipientEmail.toLowerCase())),
        [shares]
    );

    const toggleMember = (id) => {
        setSelectedMemberIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const addExternalRow = () => setExternalRows((prev) => [...prev, { name: "", email: "" }]);
    const updateExternalRow = (i, field, value) => {
        setExternalRows((prev) => prev.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
    };
    const removeExternalRow = (i) => {
        setExternalRows((prev) => prev.filter((_, idx) => idx !== i));
    };

    const handleSubmit = async () => {
        const memberRecipients = teamMembers
            .filter((m) => selectedMemberIds.has(m._id))
            .map((m) => ({
                email: m.email,
                name: `${m.firstName || ""} ${m.lastName || ""}`.trim(),
                userId: m._id,
            }));

        const externalRecipients = externalRows
            .filter((r) => r.email && emailValid(r.email))
            .map((r) => ({ email: r.email.trim(), name: r.name.trim() }));

        const merged = [];
        const seen = new Set();
        for (const r of [...memberRecipients, ...externalRecipients]) {
            const key = r.email.toLowerCase();
            if (seen.has(key)) continue;
            if (sharedEmails.has(key)) continue;
            seen.add(key);
            merged.push(r);
        }

        if (merged.length === 0) {
            setAlert({ severity: "warning", msg: "Pick at least one new recipient." });
            return;
        }

        setSubmitting(true);
        const senderName = `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() || currentUser?.email || "Your administrator";
        const res = await createShares({
            groupId,
            assessmentSlug,
            recipients: merged,
            createdBy: currentUser?._id,
            frontUrl: FRONT_URL,
            senderName,
        });
        setSubmitting(false);

        if (res.error) {
            setAlert({ severity: "error", msg: res.msg || "Failed to send" });
            return;
        }

        const createdCount = res.created?.length || 0;
        const skippedCount = res.skipped?.length || 0;
        setAlert({
            severity: createdCount > 0 ? "success" : "warning",
            msg: `${createdCount} share${createdCount === 1 ? "" : "s"} sent${skippedCount ? ` (${skippedCount} skipped)` : ""}.`,
        });
        setSelectedMemberIds(new Set());
        setExternalRows([{ name: "", email: "" }]);
        await load();
    };

    const onResend = async (s) => {
        setBusyId(s._id);
        const senderName = `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() || currentUser?.email || "Your administrator";
        const res = await resendShare(s._id, { frontUrl: FRONT_URL, senderName });
        setBusyId(null);
        if (res.error) setAlert({ severity: "error", msg: res.msg || "Failed to resend" });
        else setAlert({ severity: "success", msg: `Resent to ${s.recipientEmail}` });
        await load();
    };

    const onRevoke = async (s) => {
        if (!window.confirm(`Revoke access for ${s.recipientEmail}?`)) return;
        setBusyId(s._id);
        const res = await revokeShare(s._id, { revokedBy: currentUser?._id });
        setBusyId(null);
        if (res.error) setAlert({ severity: "error", msg: res.msg || "Failed to revoke" });
        else setAlert({ severity: "success", msg: `Revoked ${s.recipientEmail}` });
        await load();
    };

    const onCopyCode = async (s) => {
        try {
            await navigator.clipboard.writeText(s.code);
            setAlert({ severity: "success", msg: `Code copied: ${s.code}` });
        } catch {
            setAlert({ severity: "error", msg: "Couldn't copy — code: " + s.code });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Manage report access — {group?.name}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Each recipient gets a unique magic link and 6-digit access code. They can be
                    team members or outside people (e.g. HR head, executive sponsor).
                </Typography>

                {alert && (
                    <Alert
                        sx={{ mb: 2 }}
                        severity={alert.severity}
                        onClose={() => setAlert(null)}
                    >
                        {alert.msg}
                    </Alert>
                )}

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>Existing recipients</Typography>
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton onClick={load} size="small" disabled={loading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : shares.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No one has been granted access yet.
                    </Typography>
                ) : (
                    <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Recipient</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Last opened</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shares.map((s) => {
                                const st = statusFor(s);
                                const busy = busyId === s._id;
                                return (
                                    <TableRow key={s._id}>
                                        <TableCell>
                                            <Typography variant="body2">{s.recipientName || "—"}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {s.recipientEmail}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={st.label} color={st.color} variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                                    {s.code}
                                                </Typography>
                                                <Tooltip title="Copy code">
                                                    <IconButton size="small" onClick={() => onCopyCode(s)}>
                                                        <ContentCopyIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {formatDate(s.lastAccessedAt)}
                                            </Typography>
                                            {s.accessCount > 0 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                    Views: {s.accessCount}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {!s.revokedAt && (
                                                <>
                                                    <Tooltip title="Resend email">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => onResend(s)}
                                                                disabled={busy}
                                                            >
                                                                <SendIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Revoke access">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => onRevoke(s)}
                                                                disabled={busy}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {busy && <CircularProgress size={16} sx={{ ml: 1 }} />}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Add team members
                </Typography>
                {teamMembers && teamMembers.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                        {teamMembers.map((m) => {
                            const already = sharedEmails.has((m.email || "").toLowerCase());
                            const checked = selectedMemberIds.has(m._id);
                            return (
                                <Chip
                                    key={m._id}
                                    label={`${m.firstName || ""} ${m.lastName || ""}`.trim() || m.email}
                                    icon={<Checkbox checked={checked} size="small" sx={{ p: 0, ml: 0.5 }} />}
                                    variant={checked ? "filled" : "outlined"}
                                    color={checked ? "primary" : "default"}
                                    onClick={() => !already && toggleMember(m._id)}
                                    disabled={already}
                                />
                            );
                        })}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No team members yet.
                    </Typography>
                )}

                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Add people outside the team
                </Typography>
                <Stack spacing={1} sx={{ mb: 1 }}>
                    {externalRows.map((row, i) => (
                        <Stack direction="row" spacing={1} key={i} alignItems="center">
                            <TextField
                                label="Name"
                                value={row.name}
                                onChange={(e) => updateExternalRow(i, "name", e.target.value)}
                                size="small"
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Email"
                                value={row.email}
                                onChange={(e) => updateExternalRow(i, "email", e.target.value)}
                                size="small"
                                sx={{ flex: 2 }}
                                error={row.email !== "" && !emailValid(row.email)}
                            />
                            <IconButton onClick={() => removeExternalRow(i)} size="small">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}
                </Stack>
                <Button startIcon={<AddIcon />} onClick={addExternalRow} size="small">
                    Add another
                </Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={submitting}>
                    CLOSE
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                >
                    {submitting ? "Sending…" : "SAVE & SEND"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageReportAccessDialog;
